// =====================================================
// CHAT API LAYER
// Thin wrappers around the REST endpoints so components never build URLs or
// spread axios config inline. Every chat write already carries an idempotent
// clientId; block/settings calls that need auth send the Bearer token.
// =====================================================

import axios from "axios";
import { BASE_URL } from "./constants";
import { postWithRetry } from "./chatUtils";

const authHeader = (token) =>
  token ? { headers: { Authorization: `Bearer ${token}` } } : {};

// ── Users / conversation list ──
export const fetchUsers = (userId) =>
  axios.get(`${BASE_URL}/api/chat/users/${userId}`);

export const fetchMessages = (userId, otherId) =>
  axios.get(`${BASE_URL}/api/chat/${userId}/${otherId}`);

export const checkCanChat = (userId, otherId) =>
  axios.get(`${BASE_URL}/api/chat/can-chat/${userId}/${otherId}`);

// ── Send / upload ──
export const sendMessage = (payload) =>
  postWithRetry(`${BASE_URL}/api/chat`, payload);

export const uploadFile = (formData, onUploadProgress) =>
  axios.post(`${BASE_URL}/api/chat/upload`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress,
  });

// ── Edit / delete / react ──
export const editMessage = (msgId, userId, text) =>
  axios.put(`${BASE_URL}/api/chat/${msgId}/edit`, { userId, text });

export const deleteMessage = (msgId) =>
  axios.delete(`${BASE_URL}/api/chat/${msgId}`);

export const reactToMessage = (msgId, userId, emoji) =>
  axios.post(`${BASE_URL}/api/chat/reaction`, { msgId, userId, emoji });

// ── Disappearing-message settings ──
export const getConversationSettings = (userId, otherId) =>
  axios.get(`${BASE_URL}/api/chat/settings/${userId}/${otherId}`);

export const setDisappearing = (userId, otherId, disappearingSeconds) =>
  axios.put(`${BASE_URL}/api/chat/settings`, {
    userId,
    otherId,
    disappearingSeconds,
  });

// ── Chat backup (export / restore) ──
export const exportBackup = (userId) =>
  axios.get(`${BASE_URL}/api/chat/backup/${userId}`);

export const restoreBackup = (userId, messages) =>
  axios.post(`${BASE_URL}/api/chat/backup/restore`, { userId, messages });

// ── Block / unblock (auth-gated block routes) ──
export const getBlockStatus = (otherId, token) =>
  axios.get(`${BASE_URL}/api/block/status/${otherId}`, authHeader(token));

export const blockUser = (otherId, token) =>
  axios.post(`${BASE_URL}/api/block/${otherId}`, {}, authHeader(token));

export const unblockUser = (otherId, token) =>
  axios.post(`${BASE_URL}/api/block/unblock/${otherId}`, {}, authHeader(token));
